import React, { useState } from "react";
import { useSelector, useDispatch } from "react-redux";

import { ModalActions } from "src/redux/Modal/actions";
import { CounterPartysSelectors } from "src/redux/CounterPartys/selectors";
import { AuthSelectors } from "src/redux/Auth/selectors";
import { SettingsSelectors } from "src/redux/Settings/selectors";
import Button from "src/components/common/Button";
import Dropdown from "src/components/common/Dropdown";
import SelectTable from "src/components/common/SelectTable";
import Toggle from "src/components/common/Toggle";
import styles from "./styles.module.scss";

export default ({ availabilitySettings, onSave, isListLevel, listName }) => {
    const dispatch = useDispatch();
    const isTraderRole = useSelector(AuthSelectors.IsTraderRole);
    const knownCptys = useSelector(CounterPartysSelectors.SelectAll);
    // Limited UI: per spec §3 "1-to-many → 1-to-1", a lender's list may
    // advertise to AT MOST one borrower. Backend (SettingsController.
    // uploadCptyLenderListSettings) enforces this; here we cap the
    // picker to a single selection and hide "Select All" so the user
    // can't pick a multi-cpty state the server would reject.
    const { "limited-ui": limitedUi } = useSelector(SettingsSelectors.FeatureFlags);
    const {
        cptyIds: currCptyIds,
        showRate: currShowRate,
        enabled: currEnabled,
    } = availabilitySettings;

    const [cptyIds, setCptyIds] = useState(currCptyIds || []);
    const [showRate, setShowRate] = useState(currShowRate || false);
    const [enabled, setEnabled] = useState(currEnabled || false);

    const handleAllSelect = () => {
        let newCptyIds = [];
        if (!Array.isArray(cptyIds) || cptyIds.length < knownCptys.length) {
            newCptyIds = knownCptys.map((cpty) => cpty.id);
        }
        setCptyIds(newCptyIds);
    };

    const handleRowSelect = (row) => {
        let newCptyIds;
        if (Array.isArray(cptyIds)) {
            if (cptyIds.includes(row.id)) {
                newCptyIds = cptyIds.filter((cpty) => cpty !== row.id);
            } else {
                newCptyIds = [...cptyIds, row.id];
            }
        } else {
            newCptyIds = [row.id];
        }
        setCptyIds(newCptyIds);
    };

    const handleShowRateToggle = (value) => {
        setShowRate(!showRate);
    };

    const handleEnabledToggle = (value) => {
        setEnabled(!enabled);
    };

    return (
        <div className={styles.availabilitySettings}>
            {isListLevel && (
                <>
                    <div className={styles.section}>
                        <div className={styles.title}>
                            List Name: {listName}
                        </div>
                    </div>
                    <div className={styles.splitter} />
                </>
            )}
            <div className={styles.section}>
                {isListLevel ? (
                    <>
                        <div className={styles.title}>
                            Counterparties enabled for the List
                        </div>
                        <div className={styles.desc}>
                            This determines which Counterparties receive Outbox
                            messages.
                        </div>
                    </>
                ) : (
                    <div className={styles.title}>
                        Counterparties to advertise to
                    </div>
                )}

                {limitedUi ? (
                    // §3 "1-to-many → 1-to-1": single-select dropdown.
                    // Matches the lender's `Counterparty to Receive Offer`
                    // dropdown shape so the UI is consistent across the
                    // limited surfaces. SelectTable's "Select All"
                    // affordance has no meaning under cap=1, so we hide
                    // it entirely (not just disable).
                    <Dropdown
                        mainStyle="thick-black"
                        value={
                            Array.isArray(cptyIds) && cptyIds.length > 0
                                ? knownCptys.find((c) => c.id === cptyIds[0])
                                : null
                        }
                        options={knownCptys
                            .slice()
                            .sort((a, b) => a.name.localeCompare(b.name))}
                        getOptionValue={(el) => el.id}
                        getOptionLabel={(el) => el.name}
                        placeholder="Select a CPTY"
                        onChange={(cpty) => setCptyIds([cpty.id])}
                        dataTestId="availability-select-cpty"
                        ariaLabel="Select a CPTY"
                    />
                ) : (
                    <SelectTable
                        title="Counterparty"
                        rows={knownCptys
                            .sort((a, b) => a.name.localeCompare(b.name))
                            .map((i) => ({ ...i, columns: [i.name] }))}
                        selected={cptyIds || []}
                        handleAllSelect={handleAllSelect}
                        handleRowSelect={handleRowSelect}
                        noResults="No Known Counterparties"
                        styleType="availability"
                    />
                )}
            </div>

            <div className={styles.splitter} />

            <div className={styles.section}>
                {isListLevel && (
                    <div className={styles.toggleWrapper}>
                        <div className={styles.toggleLabel}>
                            Send Outbox Msg
                        </div>
                        <div className={styles.toggle}>
                            <Toggle
                                value={enabled}
                                dataTestId="availability-enable"
                                onChange={handleEnabledToggle}
                            />
                        </div>
                    </div>
                )}
                <div className={styles.toggleWrapper}>
                    <div className={styles.toggleLabel}>
                        Display Rate
                    </div>
                    <div className={styles.toggle}>
                        <Toggle
                            value={showRate}
                            dataTestId="availability-show-rate"
                            onChange={handleShowRateToggle}
                        />
                    </div>
                </div>
            </div>
            <div className={styles.footer}>
                <Button
                    value="Save"
                    onClick={() => onSave({ cptyIds, showRate, enabled })}
                    disabled={!isTraderRole}
                    dataTestId="save-availability-settings"
                />
                <Button
                    onClick={() => dispatch(ModalActions.Close())}
                    value="Cancel"
                    dataTestId="cancel-availability-settings"
                    color="reversedWhite"
                />
            </div>
        </div>
    );
};
